import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, FormControl, Grid, Paper, TextField } from '@mui/material';
import { DataGrid } from '@material-ui/data-grid';
import WestIcon from '@mui/icons-material/West';
import { useNavigate, useParams } from 'react-router';
import MainContainer from '../../../components/MainContainer';
import { headerDataMission } from '../../../data/modules/mission-header';
import Title from '../../../components/Title';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import TimeSheetMissionService from '../../../services/TimeSheetMissionService';
import Spacer from '../../../components/Spacer';
import SearchIcon from '@mui/icons-material/Search';
import { DownloadOutlined } from "@mui/icons-material";


const formatDate = (value, includeTime = false) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = date.getFullYear();
  if (!includeTime) {
    return `${day}/${month}/${year}`;
  }
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const resolveFieldLabel = (row = {}) =>
  row.field_label ||
  row.field ||
  row.column_label ||
  row.column ||
  row.target_field ||
  row.attribute ||
  'Champ';

const resolveAction = (row = {}) =>
  row.action_label ||
  row.action ||
  row.event ||
  row.operation ||
  row.status ||
  'Modification';

const resolveUser = (row = {}) =>
  row.user_name || row.name || row.created_by || row.utilisateur || row.user || '';

const resolveDetails = (row = {}) => {
  if (row.description) {
    return row.description;
  }
  if (row.destination_label) {
    return row.destination_label;
  }
  const parts = [
    row.date_begin || row.date_end
      ? `(${formatDate(row.date_begin)} - ${formatDate(row.date_end)})`
      : '',
    row.depart || row.depart_label,
    row.arrivee || row.arrivee_label,
    row.direction || row.direction_code,
    row.region,
  ].filter(Boolean);
  return parts.join(' ').trim();
};

const resolveFileUrl = (filepath) => {
  if (!filepath || typeof filepath !== 'string') {
    return '';
  }
  if (/^https?:\/\//i.test(filepath)) {
    return filepath;
  }

  const normalized = filepath.replace(/\\/g, '/');
  const uploadsIndex = normalized.toLowerCase().lastIndexOf('/uploads/');
  if (uploadsIndex === -1) {
    return normalized;
  }

  const relativePath = normalized.slice(uploadsIndex + 1);
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const baseUrl = apiUrl.replace(/\/api\/?$/i, '/');

  if (!baseUrl) {
    return relativePath;
  }

  try {
    return new URL(relativePath, baseUrl).toString();
  } catch (error) {
    return relativePath;
  }
};

const getFilename = (filepath) => {
  if (!filepath || typeof filepath !== 'string') {
    return 'fichier';
  }
  const normalized = filepath.replace(/\\/g, '/');
  const parts = normalized.split('/');
  return parts[parts.length - 1] || 'fichier';
};

const downloadFile = (filepath) => {
  const url = resolveFileUrl(filepath);
  if (!url) {
    return;
  }
  const filename = getFilename(filepath);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


const TimesheetHistory = () => {
  const { mission_timesheet_id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    user_name: '',
    debut: '',
    fin: '',
  });

  useEffect(() => {
    const fetchHistory = async () => {
      if (!mission_timesheet_id) {
        return;
      }
      setLoading(true);
      try {
        const response = await TimeSheetMissionService.getHistoryTimesheetMissions(
          mission_timesheet_id,
          filter
        );
        if (response?.status) {
          const history = Array.isArray(response.data) ? response.data : [];
          setRows(
            history.map((item, index) => ({
              id: item.mission_timesheet_history_id || item.id || index,
              ...item,
            })),
          );
        } else {
          setRows([]);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to fetch timesheet history', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [mission_timesheet_id]);

  const columns = useMemo(
    () => [
       {
        field: 'created_at',
        headerName: 'Date',
        flex: 0.7,
        minWidth: 120,
        renderCell: (params) => (
          <span>{formatDate(params.row.created_at || params.row.date, true)}</span>
        ),
      },
       {
        field: 'action',
        headerName: 'Action',
        flex: 0.7,
        minWidth: 280,
        renderCell: (params) => <span>{resolveAction(params.row)}</span>,
      },
       {
        field: 'user_name',
        headerName: 'Utilisateur',
        flex: 0.7,
        minWidth: 200,
        renderCell: (params) => <span>{resolveUser(params.row)}</span>,
      },
      {
        field: 'line',
        headerName: "Ligne d'agenda / Destination",
        flex: 1.5,
        minWidth: 180,
        renderCell: (params) => <span>{resolveDetails(params.row)}</span>,
      },
      // {
      //   field: 'field',
      //   headerName: 'Champ',
      //   flex: 0.8,
      //   minWidth: 160,
      //   renderCell: (params) => <span>{resolveFieldLabel(params.row)}</span>,
      // },
      {
        field: 'old_value',
        headerName: 'Ancienne valeur',
        flex: 0.8,
        minWidth: 150,
        renderCell: (params) => (
          <span>{params.row.old_value ?? params.row.previous_value ?? ''}</span>
        ),
      },
      {
        field: 'new_value',
        headerName: 'Nouvelle valeur',
        flex: 0.8,
        minWidth: 150,
        renderCell: (params) => {
          if(params.row.action === 'Génération fichier pdf' || params.row.action === 'Génération fichier excel'){
            return <Button size='small' sx={{ textTransform: 'none' }}
                            startIcon={<DownloadOutlined />}
                            onClick={() => downloadFile(params.row.new_value)} color="info"
                        >
                            Télécharger Fichier
                        </Button>
          } else {

          return <span>{params.row.new_value ?? params.row.current_value ?? ''}</span>;
          }
        },
      },
     
    ],
    [],
  );

  const handleChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  }

  return (
    <MainContainer {...headerDataMission}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={loading} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
            Utilisateur :
            <TextField
            fullWidth
            id="user_name"
            size="small"
            variant="outlined"
            value={filter.user_name}
            name="user_name"
            onChange={handleChangeFilter}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                Date début:
                <TextField
                fullWidth
                id="date_debut"
                size="small"
                variant="outlined"
                type="date"
                value={filter.debut}
                name="debut"
                onChange={handleChangeFilter}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                Date fin:
                <TextField
                fullWidth
                id="date_fin"
                size="small"
                variant="outlined"
                type="date"
                value={filter.fin}
                name="fin"
                onChange={handleChangeFilter}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3} display="flex" alignItems="center" justifyContent="space-between">
              {/* <Box display="flex" justifyContent="space-between" alignItems="center"> */}
                 <Button variant="contained" color="secondary" onClick={() => {
          // Trigger re-fetch with current filter
          setFilter({ ...filter });
        }}
        startIcon={<SearchIcon />} marginTop={2}
        >
          Filtrer
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(-1)} marginTop={2}
        >
          <WestIcon sx={{ mr: 1 }} />
          Retour
        </Button>
     
      {/* </Box> */}
      </Grid>
      </Grid>
      <Spacer y={1} />
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Title>Historique des modifications</Title>
        <div style={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default TimesheetHistory;
